import React, { useState } from "react";
import { Menu, X, Star } from "lucide-react";
import EziMark from "./EziMark";

const NAV = [
  { label: "Domain Search", href: "/" },
  { label: "API", href: "#" },
  { label: "Pricing", href: process.env.REACT_APP_PRICING_URL || "#" },
  { label: "Status", href: "#" },
];

// External eziwebb.com destinations (configurable via env)
const SIGN_IN_URL = process.env.REACT_APP_SIGN_IN_URL || "#";
const GET_STARTED_URL = process.env.REACT_APP_GET_STARTED_URL || "#";

function Brand() {
  return (
    <a href="/" className="flex items-center gap-2.5">
      <EziMark className="h-9 w-9" />
      <span className="text-xl font-bold tracking-tight lowercase">
        <span className="text-[#E8B62A]">ezidomains</span>
        <span className="text-zinc-500">.info</span>
      </span>
    </a>
  );
}

function DesktopNav() {
  return (
    <nav className="hidden items-center gap-8 md:flex">
      {NAV.map((n) => (
        <a
          key={n.label}
          href={n.href}
          {...(n.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
          className="text-sm text-zinc-400 transition-colors hover:text-white"
        >
          {n.label}
        </a>
      ))}
    </nav>
  );
}

function AuthButtons() {
  return (
    <div className="hidden items-center gap-3 md:flex">
      <a
        href={SIGN_IN_URL}
        target="_blank"
        rel="noreferrer"
        className="text-sm text-zinc-400 transition-colors hover:text-white"
      >
        Sign in
      </a>
      <a
        href={GET_STARTED_URL}
        target="_blank"
        rel="noreferrer"
        className="rounded-md bg-white px-3.5 py-1.5 text-sm font-medium text-black transition-colors hover:bg-zinc-200"
      >
        Get started
      </a>
    </div>
  );
}

function MobileMenu() {
  return (
    <div className="border-t border-white/10 bg-[#0a0a0b] px-5 py-4 md:hidden">
      <div className="flex flex-col gap-4">
        {NAV.map((n) => (
          <a
            key={n.label}
            href={n.href}
            {...(n.href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {})}
            className="text-sm text-zinc-300"
          >
            {n.label}
          </a>
        ))}
        <div className="flex items-center gap-3 pt-2">
          <a href={SIGN_IN_URL} target="_blank" rel="noreferrer" className="text-sm text-zinc-300">
            Sign in
          </a>
          <a
            href={GET_STARTED_URL}
            target="_blank"
            rel="noreferrer"
            className="rounded-md bg-white px-3 py-1.5 text-sm font-medium text-black"
          >
            Get started
          </a>
        </div>
      </div>
    </div>
  );
}

function SavedButton({ onOpenFavourites, count }) {
  return (
    <button
      onClick={onOpenFavourites}
      data-testid="open-favourites-btn"
      className="relative flex items-center gap-1.5 rounded-md border border-white/10 bg-white/[0.03] px-3 py-1.5 text-sm text-zinc-300 transition-colors hover:border-white/20 hover:text-white"
    >
      <Star className="h-4 w-4" />
      <span className="hidden sm:inline">Saved</span>
      {count > 0 && (
        <span
          data-testid="favourites-count"
          className="ml-0.5 rounded-full bg-amber-400 px-1.5 text-[10px] font-bold text-black"
        >
          {count}
        </span>
      )}
    </button>
  );
}

export default function Header({ onOpenFavourites, favouritesCount = 0 }) {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0a0a0b]/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5">
        <Brand />
        <DesktopNav />
        <div className="flex items-center gap-3">
          <SavedButton onOpenFavourites={onOpenFavourites} count={favouritesCount} />
          <AuthButtons />
          <button
            className="text-zinc-300 md:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && <MobileMenu />}
    </header>
  );
}
